import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Card,
  Row,
  Col,
  Select,
  DatePicker as AntDatePicker,
  message,
  Typography,
  Modal,
  Form,
  InputNumber,
  Input
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ClearOutlined
} from '@ant-design/icons';
import { measurementsAPI, adminAPI } from '../../utils/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

const Measurements = () => {
  const [measurements, setMeasurements] = useState([]);
  const [filteredMeasurements, setFilteredMeasurements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [consumers, setConsumers] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  // Filter states
  const [searchText, setSearchText] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Debounced search text
  const [debouncedSearchText, setDebouncedSearchText] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    fetchConsumers();
  }, []);

  useEffect(() => {
    fetchMeasurements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchText, startDate, endDate]);

  const fetchMeasurements = async () => {
    try {
      setLoading(true);
      const params = {
        limit: 100,
        search: debouncedSearchText
      };

      if (startDate && endDate) {
        params.start_date = dayjs(startDate).format('YYYY-MM-DD');
        params.end_date = dayjs(endDate).format('YYYY-MM-DD');
      }

      const response = await measurementsAPI.getMeasurements(params);
      const fetchedMeasurements = response.data.measurements || [];
      setMeasurements(fetchedMeasurements);
      setFilteredMeasurements(fetchedMeasurements);
    } catch (error) {
      console.error('Fetch measurements error:', error);
      message.error('Failed to fetch health measurements');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchText('');
    setStartDate(null);
    setEndDate(null);
  };



  const fetchConsumers = async () => {
    try {
      const response = await adminAPI.getConsumers({
        is_active: true,
        limit: 1000
      });
      setConsumers(response.data.consumers || []);
    } catch (error) {
      message.error('Failed to fetch consumers');
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingRecord) {
        await measurementsAPI.updateMeasurement(editingRecord.id, {
          ...values,
          measurement_date: values.measurement_date ? values.measurement_date.format('YYYY-MM-DD') : undefined
        });
        message.success('Health measurement updated successfully');
      } else {
        await measurementsAPI.createMeasurement({
          ...values,
          measurement_date: values.measurement_date.format('YYYY-MM-DD')
        });
        message.success('Health measurement recorded successfully');
      }

      setModalVisible(false);
      setEditingRecord(null);
      form.resetFields();
      fetchMeasurements();
    } catch (error) {
      message.error(`Failed to ${editingRecord ? 'update' : 'record'} health measurement`);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      ...record,
      measurement_date: dayjs(record.measurement_date)
    });
    setModalVisible(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete Health Measurement',
      content: 'Are you sure you want to delete this health measurement?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await measurementsAPI.deleteMeasurement(record.id);
          message.success('Health measurement deleted successfully');
          fetchMeasurements();
        } catch (error) {
          message.error('Failed to delete health measurement');
        }
      }
    });
  };

  const showAddModal = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      measurement_date: dayjs()
    });
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'Consumer',
      dataIndex: 'consumer_name',
      key: 'consumer_name',
    },
    {
      title: 'Date',
      dataIndex: 'measurement_date',
      key: 'measurement_date',
      render: (date) => dayjs(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Weight (kg)',
      dataIndex: 'weight',
      key: 'weight',
      render: (weight) => weight || '-',
    },
    {
      title: 'Height (cm)',
      dataIndex: 'height',
      key: 'height',
      render: (height) => height || '-',
    },
    {
      title: 'BMI',
      dataIndex: 'bmi',
      key: 'bmi',
      render: (bmi) => (bmi && !isNaN(parseFloat(bmi)) && parseFloat(bmi) > 0) ? parseFloat(bmi).toFixed(1) : '-',
    },
    {
      title: 'Body Fat %',
      dataIndex: 'body_fat_percentage',
      key: 'body_fat_percentage',
      render: (percentage) => percentage ? `${percentage}%` : '-',
    },
    {
      title: 'Blood Pressure',
      key: 'blood_pressure',
      render: (_, record) => {
        if (record.blood_pressure_systolic && record.blood_pressure_diastolic) {
          return `${record.blood_pressure_systolic}/${record.blood_pressure_diastolic}`;
        }
        return '-';
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            size="small"
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }} wrap={false}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Health Measurements
          </Title>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchMeasurements}
              loading={loading}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showAddModal}
            >
              Add Measurement
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Filter Section */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Search by consumer name..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={10}>
            <AntDatePicker.RangePicker
              style={{ width: '100%' }}
              value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null}
              onChange={(dates) => {
                if (dates) {
                  setStartDate(dates[0].toDate());
                  setEndDate(dates[1].toDate());
                } else {
                  setStartDate(null);
                  setEndDate(null);
                }
              }}
              format="YYYY-MM-DD"
            />
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Space>
              <Button
                icon={<ClearOutlined />}
                onClick={clearFilters}
                disabled={!searchText && !startDate && !endDate}
              >
                Clear Filters
              </Button>
              <Typography.Text type="secondary">
                Showing {filteredMeasurements.length} of {measurements.length} records
              </Typography.Text>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={filteredMeasurements}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title={editingRecord ? 'Edit Health Measurement' : 'Add Health Measurement'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="consumer_id"
                label="Consumer"
                rules={[{ required: true, message: 'Please select a consumer' }]}
              >
                <Select
                  placeholder="Select consumer"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {consumers.map(consumer => (
                    <Option key={consumer.id} value={consumer.id}>
                      {consumer.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              {/* Keep Ant Design DatePicker for the form input */}
              <Form.Item
                name="measurement_date"
                label="Measurement Date"
                rules={[{ required: true, message: 'Please select measurement date' }]}
              >
                <AntDatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="weight" label="Weight (kg)">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={1}
                  placeholder="Enter weight"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="height" label="Height (cm)">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={300}
                  precision={1}
                  placeholder="Enter height"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="body_fat_percentage" label="Body Fat %">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                  precision={1}
                  placeholder="Enter body fat %"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="muscle_mass" label="Muscle Mass (kg)">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={200}
                  precision={1}
                  placeholder="Enter muscle mass"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="blood_pressure_systolic" label="Blood Pressure (Systolic)">
                <InputNumber
                  style={{ width: '100%' }}
                  min={50}
                  max={300}
                  placeholder="Enter systolic"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="blood_pressure_diastolic" label="Blood Pressure (Diastolic)">
                <InputNumber
                  style={{ width: '100%' }}
                  min={30}
                  max={200}
                  placeholder="Enter diastolic"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="heart_rate" label="Heart Rate (bpm)">
                <InputNumber
                  style={{ width: '100%' }}
                  min={30}
                  max={220}
                  placeholder="Enter heart rate"
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={3} placeholder="Optional notes" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingRecord ? 'Update' : 'Add'} Measurement
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Measurements;
